import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Caption,
  Card,
  Chart,
  HeroFigure,
  LoadingState,
  Split,
  Stack,
  Table,
  outcomeColors,
  useChartColors
} from '@srichaitra/ui';
import { labels } from '@srichaitra/constants';
import {
  loadDashboard,
  selectCanSeeBusinessStatus,
  selectDashboardError,
  selectDashboardFigures,
  selectDashboardStatus,
  useAppDispatch,
  useAppSelector
} from '@srichaitra/store';
import { Section } from './Section';
import { formatCompactCurrency, formatCurrency, formatSignedCurrency } from './format';

/**
 * Profit and loss: the answer on the left, the arithmetic that produced it on
 * the right.
 *
 * The split is the point. A reader who takes one thing from this dashboard
 * should take the profit figure, so it gets the hero treatment and nothing else
 * on the page competes at that size. Anyone who wants to know *why* reads
 * across the four bars beside it — sales, the two things taken off the top, and
 * what is left.
 *
 * The figures come from the server's finance.businessStatus and are not
 * recomputed here. Profit matches the cost of stock to what actually *sold*
 * rather than to what was bought — a decision made once, on the server, and
 * shared with the mobile client.
 *
 * Colour does three jobs and no more: money in, money out, and the outcome. The
 * outcome is the only thing in the application coloured by judgement, and it
 * always travels with an icon and the word "Profit" or "Loss", so nothing
 * depends on separating green from red.
 */
export function ProfitLossPanel() {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectDashboardStatus);
  const error = useAppSelector(selectDashboardError);
  const figures = useAppSelector(selectDashboardFigures);
  const entitled = useAppSelector(selectCanSeeBusinessStatus);
  const seriesColors = useChartColors();

  const [asTable, setAsTable] = useState(false);

  useEffect(() => {
    dispatch(loadDashboard({ canSeeBusinessStatus: entitled }));
  }, [dispatch, entitled]);

  if (status === 'idle' || (status === 'loading' && !figures)) {
    return (
      <Section title={labels.profitLoss.heading}>
        <LoadingState message={labels.common.loading} />
      </Section>
    );
  }

  if (status === 'failed') {
    return (
      <Section title={labels.profitLoss.heading}>
        <Alert tone="error">{error?.message}</Alert>
      </Section>
    );
  }

  // The server withheld the figures, or never had them to give. Said plainly
  // rather than drawn as a chart of zeroes.
  if (!figures || figures.scope !== 'full') {
    return (
      <Section title={labels.profitLoss.heading}>
        <Alert tone="info">{labels.profitLoss.restricted}</Alert>
      </Section>
    );
  }

  const isLoss = Number(figures.profit) < 0;

  const rows = [
    { key: 'sales', label: labels.profitLoss.sales, value: figures.sales, role: 'in' },
    {
      key: 'costOfStockSold',
      label: labels.profitLoss.costOfStockSold,
      value: figures.stockSpending,
      role: 'out'
    },
    { key: 'miscExpenses', label: labels.profitLoss.miscExpenses, value: figures.miscSpending, role: 'out' },
    {
      key: 'result',
      label: isLoss ? labels.profitLoss.loss : labels.profitLoss.profit,
      value: figures.profit,
      role: 'result'
    }
  ];

  // Money in and money out each get one colour — the two cost bars share theirs
  // because they are the same kind of thing. Only the result is judged.
  const colorForRole = {
    in: seriesColors[0],
    out: seriesColors[1],
    result: isLoss ? outcomeColors.bad : outcomeColors.good
  };

  return (
    <Section
      title={labels.profitLoss.heading}
      actions={
        <Button variant="secondary" size="sm" onClick={() => setAsTable((current) => !current)}>
          {asTable ? labels.profitLoss.showChart : labels.profitLoss.showTable}
        </Button>
      }
    >
      <Split ratio="minmax(240px, 1fr) 2fr">
        <Card padding={6}>
          <HeroFigure
            label={labels.profitLoss.resultLabel}
            status={isLoss ? 'bad' : 'good'}
            statusLabel={isLoss ? labels.profitLoss.loss : labels.profitLoss.profit}
            value={formatCurrency(Math.abs(figures.profit), { whole: true })}
            caption={labels.profitLoss.heroCaption}
          />
        </Card>

        <Card padding={4}>
          <Stack gap={2}>
            {asTable ? (
              <Table
                caption={labels.profitLoss.heading}
                columns={[
                  { key: 'label', header: labels.profitLoss.columns.item },
                  {
                    key: 'value',
                    header: labels.profitLoss.columns.amount,
                    render: (row) =>
                      row.role === 'result'
                        ? formatSignedCurrency(row.value, { whole: true })
                        : formatCurrency(row.value, { whole: true })
                  }
                ]}
                rows={rows}
                rowKey={(row) => row.key}
                emptyMessage={labels.trends.noActivity}
              />
            ) : (
              <Chart
                horizontal
                categories={rows.map((row) => row.label)}
                categoryColors={rows.map((row) => colorForRole[row.role])}
                series={[
                  { id: 'amount', label: labels.profitLoss.heading, data: rows.map((row) => row.value) }
                ]}
                height={260}
                valueFormatter={(value) => formatCurrency(value, { whole: true })}
                axisFormatter={formatCompactCurrency}
                ariaLabel={labels.profitLoss.heading}
              />
            )}

            <Caption>{labels.profitLoss.caption}</Caption>
          </Stack>
        </Card>
      </Split>
    </Section>
  );
}
